import { useCallback, useEffect, useState } from "react";
import { ConnectMusic } from "../ui/ConnectMusic";
import { KeySignIn } from "../ui/KeySignIn";
import { Shell } from "../ui/Shell";
import { api, makePlayer, type Player, PlayerContext } from "./services";

type Phase =
  | { kind: "signedOut" }
  | { kind: "loadingMusic" }
  | { kind: "musicFailed"; message: string }
  | { kind: "ready"; player: Player };

/** Signed out → key; signed in → load MusicKit → ask for Apple Music → the player. */
export function App() {
  const [phase, setPhase] = useState<Phase>(() =>
    api.isSignedIn ? { kind: "loadingMusic" } : { kind: "signedOut" },
  );
  const [isMusicAuthorized, setMusicAuthorized] = useState(false);

  useEffect(() => {
    api.onSignedOut = () => setPhase({ kind: "signedOut" });
  }, []);

  const loadPlayer = useCallback(async () => {
    setPhase({ kind: "loadingMusic" });
    try {
      const player = await makePlayer();
      setMusicAuthorized(player.music.isAuthorized);
      setPhase({ kind: "ready", player });
    } catch (error) {
      setPhase({
        kind: "musicFailed",
        message: error instanceof Error ? error.message : "Apple Music did not load.",
      });
    }
  }, []);

  useEffect(() => {
    if (phase.kind === "loadingMusic") void loadPlayer();
  }, [phase.kind, loadPlayer]);

  switch (phase.kind) {
    case "signedOut":
      return <KeySignIn onSignedIn={() => setPhase({ kind: "loadingMusic" })} />;
    case "loadingMusic":
      return <div className="splash">Loading Apple Music…</div>;
    case "musicFailed":
      return (
        <div className="splash">
          <p>{phase.message}</p>
          <button type="button" onClick={() => void loadPlayer()}>
            Try Again
          </button>
        </div>
      );
    case "ready":
      return (
        <PlayerContext.Provider value={phase.player}>
          {isMusicAuthorized ? (
            <Shell />
          ) : (
            <ConnectMusic onConnected={() => setMusicAuthorized(true)} />
          )}
        </PlayerContext.Provider>
      );
  }
}
